import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { KYCService } from '@/features/kyc/services/KYCService';
import { PayinService } from '@/services/PayinService';
import { WalletService } from '@/features/wallets/services/WalletService';
import { closeModal } from '@/store/actions/core.actions';
import { IPayin } from '@/store/models/payin.model';
import { IKycDocument, IKycDocumentPreview, TKycDocumentStatusType } from '@/features/kyc/store/models/kyc.model';
import { IUser } from '@/features/user/store/user.model';
import { ProfileKycActionTypes } from '@/features/kyc/store/types/profile-kyc.types';
import { TCoreAction } from '@/store/types/modals.types';
import { TAppError } from '@/store/types/base.state';
import { IProfileState, TProfileAction } from '@/store/types/profile.types';

export const findUsersKycDocuments = (user: IUser): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileKycActionTypes.GET_KYC_DOCUMENTS,
    userId: user.userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileKycActionTypes.GET_KYC_DOCUMENTS_FAIL,
    userId: user.userId,
    error,
    documentation: 'https://docs.treezor.com/guide/users/documents.html',
  });
  const success = (documents: IKycDocument[]): TProfileAction => ({
    type: ProfileKycActionTypes.GET_KYC_DOCUMENTS_SUCCESS,
    shouldSkipToaster: true,
    userId: user.userId,
    documents,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const { documents } = await KYCService.findUserKycDocuments(user);

      dispatch(success(documents));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const deleteUserKycDocument = (
  document: IKycDocument,
  onSuccess?: () => void,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileKycActionTypes.REMOVE_KYC_DOCUMENT,
    document,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileKycActionTypes.REMOVE_KYC_DOCUMENT_FAIL,
    document,
    error,
    documentation: 'https://docs.treezor.com/guide/users/documents.html',
  });
  const success = (document: IKycDocument): TProfileAction => ({
    type: ProfileKycActionTypes.REMOVE_KYC_DOCUMENT_SUCCESS,
    document,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      await KYCService.deleteUserKycConnectDocument(document.documentId);
      dispatch(success(document));
      onSuccess?.();
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const addUserKycDocument = (
  userId: number,
  documentTypeId: number,
  file: File,
  batchIndex?: string,
  shouldSkipToasterOnSuccess?: boolean,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction | TCoreAction> => {
  const request = (): TProfileAction => ({
    type: ProfileKycActionTypes.ADD_KYC_DOCUMENT,
    userId,
    batchIndex,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileKycActionTypes.ADD_KYC_DOCUMENT_FAIL,
    userId,
    error,
    batchIndex,
    documentation: 'https://docs.treezor.com/guide/users/documents.html#upload',
  });
  const success = (document: IKycDocument): TProfileAction => ({
    type: ProfileKycActionTypes.ADD_KYC_DOCUMENT_SUCCESS,
    document,
    batchIndex,
    shouldSkipToaster: shouldSkipToasterOnSuccess,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction | TCoreAction>): Promise<void> => {
    dispatch(request());
    try {
      const document = await KYCService.addUserKycDocument(documentTypeId, userId, file, batchIndex);
      // await KYCService.submitConnectKycReview(userId); // TODO Enable after fix this endpoint
      dispatch(success(document));
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const reviewDocumentKyc = (
  status: TKycDocumentStatusType,
  document: IKycDocument,
  comment: string,
  user?: IUser,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    const documentStatus = status === 'VALIDATED' ? 2 : 1;
    return dispatch(editKycStatusDocument(document as IKycDocument, documentStatus, comment, user));
  };
};

export const editKycStatusDocument = (
  document: IKycDocument,
  status: number,
  comment: string,
  user?: IUser,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileKycActionTypes.EDIT_KYC_DOCUMENT,
    document,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileKycActionTypes.EDIT_KYC_DOCUMENT_FAIL,
    document,
    error,
    documentation: 'https://docs.treezor.com/guide/users/kyc.html#doing-your-part',
  });
  const success = (document: IKycDocument): TProfileAction => ({
    type: ProfileKycActionTypes.EDIT_KYC_DOCUMENT_SUCCESS,
    document,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const documentKyc = await KYCService.editKycStatusDocument(document.documentId, status, comment);
      dispatch(success(documentKyc));
      user && dispatch(findUsersKycDocuments(user));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getPreviewKycDocument = (
  userId: number,
  document: IKycDocument,
  onSuccess?: (previewUrl: string) => void,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileKycActionTypes.GET_KYC_DOCUMENT_PREVIEW,
    userId,
    document,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileKycActionTypes.GET_KYC_DOCUMENT_PREVIEW_FAIL,
    userId,
    document,
    error,
    documentation: 'https://docs.treezor.com/guide/users/documents.html#download',
  });
  const success = (document: IKycDocument, preview: IKycDocumentPreview): TProfileAction => ({
    type: ProfileKycActionTypes.GET_KYC_DOCUMENT_PREVIEW_SUCCESS,
    shouldSkipToaster: true,
    userId,
    document,
    preview,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const documentId = document.metadata?.connectDocumentId
        ? document.metadata?.connectDocumentId
        : document.documentId;

      const preview = await KYCService.getPreviewKycDocument(documentId);
      dispatch(success(document, preview));
      onSuccess?.(preview.url);
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getKYCProfilePayins = (userId: number): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileKycActionTypes.GET_KYC_PROFILE_PAYINS,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileKycActionTypes.GET_KYC_PROFILE_PAYINS_FAIL,
    userId,
    shouldSkipToaster: true,
    error,
  });
  const success = (payins: IPayin[]): TProfileAction => ({
    type: ProfileKycActionTypes.GET_KYC_PROFILE_PAYINS_SUCCESS,
    shouldSkipToaster: true,
    userId,
    payins,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const wallets = await WalletService.searchWallets({ userId });

      const payinsUnformatted: IPayin[][] = await Promise.all(
        wallets.map((wallet) => PayinService.getPayins({ walletId: String(wallet.walletId) })),
      );

      const payins = payinsUnformatted.reduce((acc: IPayin[], payins) => [...acc, ...(payins as IPayin[])], []);

      dispatch(success(payins));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const submitKycReview = (userId: number): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileKycActionTypes.SUBMIT_KYC,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileKycActionTypes.SUBMIT_KYC_FAIL,
    userId,
    error,
    documentation: 'https://docs.treezor.com/guide/users/documents.html#asking-user-review',
  });
  const success = (user: IUser): TProfileAction => ({
    type: ProfileKycActionTypes.SUBMIT_KYC_SUCCESS,
    user,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const user = await KYCService.submitKycReview(userId);
      dispatch(success(user));

      dispatch(findUsersKycDocuments(user));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const addChildUserKycDocument = (
  parentId: number,
  userId: number,
  documentTypeId: number,
  file: File,
  batchIndex?: string,
  shouldSkipToasterOnSuccess?: boolean,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction | TCoreAction> => {
  const request = (): TProfileAction => ({
    type: ProfileKycActionTypes.ADD_CHILD_KYC_DOCUMENT,
    parentId,
    userId,
    batchIndex,
  });

  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileKycActionTypes.ADD_CHILD_KYC_DOCUMENT_FAIL,
    parentId,
    userId,
    error,
    batchIndex,
    documentation: 'https://docs.treezor.com/guide/users/documents.html#upload',
  });

  const success = (document: IKycDocument): TProfileAction => ({
    type: ProfileKycActionTypes.ADD_CHILD_KYC_DOCUMENT_SUCCESS,
    userId,
    parentId,
    document,
    batchIndex,
    shouldSkipToaster: shouldSkipToasterOnSuccess,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction | TCoreAction>): Promise<void> => {
    dispatch(request());
    try {
      const document = await KYCService.addUserKycDocument(documentTypeId, userId, file, batchIndex);
      dispatch(success(document));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
